import { Charges } from '../components/Price/components/TillReceipt/TillReceipt';
import { roundToDecimal } from 'shared/utils/roundToDecimal';
import { IPriceData, IQuote } from 'Quotes/types/store';
import { IPType } from 'Quotes/QuoteBuilder/components/Configure/DIAConfig/types/ip';
import { PortCost } from 'Quotes/types/pricedQuote';
import { getAdditionalPrice } from 'Quotes/selectors';

interface CostUtilitiesSelectPriceProps {
  a_end_gea_cablelink_annual_cost?: number | null;
  a_end_gea_cablelink_install_cost?: number | null;
  b_end_gea_cablelink_annual_cost?: number | null;
  b_end_gea_cablelink_install_cost?: number | null;
  margin_amount?: number | null;
  margin_percentage_with_fttp_aggregation?: number | null;
  margin_percentage?: number | null;
  net_amortised_annual_price_with_fttp_aggregation?: number | null;
  net_amortised_annual_price?: number | null;
  net_annual_price_with_fttp_aggregation?: number | null;
  net_annual_price?: number | null;
  total_contract_value_with_fttp_aggregation?: number | null;
  total_contract_value?: number | null;
}

export const totalGEACablelinkCostAnnual = (selectedPrice: CostUtilitiesSelectPriceProps) => {
  return (selectedPrice.a_end_gea_cablelink_annual_cost ?? 0) + (selectedPrice.b_end_gea_cablelink_annual_cost ?? 0);
};

export const totalGEACablelinkCostInstall = (selectedPrice: CostUtilitiesSelectPriceProps) => {
  return (selectedPrice.a_end_gea_cablelink_install_cost ?? 0) + (selectedPrice.b_end_gea_cablelink_install_cost ?? 0);
};

export const roundTo2DecimalPlaces = (marginPercentage: number) => roundToDecimal(marginPercentage * 100, 2);
const toNum = (num: number | null | undefined) => num ?? 0;

export const getChargesFromSecondarySelectedPrice = (
  secondarySelectedPrice: IPriceData,
  includeFTTPAggregationCharge: boolean
): Charges => {
  const netAnnualPriceKey = includeFTTPAggregationCharge
    ? 'net_annual_price_with_fttp_aggregation'
    : 'net_annual_price';
  const netAmortisedAnnualPriceKey = includeFTTPAggregationCharge
    ? 'net_amortised_annual_price_with_fttp_aggregation'
    : 'net_amortised_annual_price';
  const marginPercentageKey = includeFTTPAggregationCharge
    ? 'margin_percentage_with_fttp_aggregation'
    : 'margin_percentage';

  const netAnnualPrice = toNum(secondarySelectedPrice[netAnnualPriceKey]);
  const netAmortisedAnnualPrice = toNum(secondarySelectedPrice[netAmortisedAnnualPriceKey]);

  // the backend prices the secondary circuit in full (IP, router, rack mount, engineer install, costs and margin)
  const totalContractValue = toNum(
    includeFTTPAggregationCharge
      ? secondarySelectedPrice.total_contract_value_with_fttp_aggregation
      : secondarySelectedPrice.total_contract_value
  );
  const marginAmount = toNum(secondarySelectedPrice.margin_amount);

  return {
    annualPrice: netAnnualPrice,
    amortisedPrice: netAmortisedAnnualPrice,
    marginPercentage: roundTo2DecimalPlaces(secondarySelectedPrice[marginPercentageKey] ?? 0),
    totalContractValue: totalContractValue,
    marginAmount: marginAmount,
  };
};

export const getChargesFromSelectedPrice = (
  quote: IQuote,
  selectedPrice: IPriceData,
  includeFTTPAggregationCharge: boolean
): Charges => {
  if (includeFTTPAggregationCharge) {
    return {
      annualPrice: selectedPrice.net_annual_price_with_fttp_aggregation ?? 0,
      amortisedPrice: selectedPrice.net_amortised_annual_price_with_fttp_aggregation ?? 0,
      marginPercentage: roundTo2DecimalPlaces(selectedPrice.margin_percentage_with_fttp_aggregation ?? 0),
      totalContractValue: selectedPrice.total_contract_value_with_fttp_aggregation ?? 0,
      marginAmount: selectedPrice.margin_amount ?? 0,
      rapidActivationPrice: getAdditionalPrice(selectedPrice.additional_prices, 'Rapid Activation Price')?.install ?? 0,
      ddosProtectionPrice: getAdditionalPrice(selectedPrice.additional_prices, `DDoS Protection - ${quote.ddosProtection} Price`)?.install,
      ddosProtectionCost: getAdditionalPrice(selectedPrice.additional_prices, `DDoS Protection - ${quote.ddosProtection} Cost`)?.install,
    };
  } else {
    return {
      annualPrice: selectedPrice.net_annual_price ?? 0,
      marginPercentage: roundTo2DecimalPlaces(selectedPrice.margin_percentage ?? 0),
      totalContractValue: selectedPrice.total_contract_value ?? 0,
      amortisedPrice: selectedPrice.net_amortised_annual_price ?? 0,
      marginAmount: selectedPrice.margin_amount ?? 0,
      rapidActivationPrice: getAdditionalPrice(selectedPrice.additional_prices, 'Rapid Activation Price')?.install ?? 0,
      ddosProtectionPrice: getAdditionalPrice(selectedPrice.additional_prices, `DDoS Protection - ${quote.ddosProtection} Price`)?.install,
      ddosProtectionCost: getAdditionalPrice(selectedPrice.additional_prices, `DDoS Protection - ${quote.ddosProtection} Cost`)?.install,
    };
  }
};

interface SelectedPriceProps {
  amortised?: boolean | null;
  fttp_aggregation_charge?: number | null;
  annual_ip_charge?: number | null;
  mdia_annual_price?: number | null;
  mdia_engineer_price?: number | null;
  mdia_install_price?: number | null;
  mdia_rack_mount_kit_price?: number | null;
  mdia_router_price?: number | null;
  annual_discount?: number | null;
  additional_prices?: PortCost[] | null;
}

export interface AnnualExtras {
  fttpAggregationCharge: number;
  ipCost: number;
  mdiaAnnualPrice: number;
  annualDiscount: number;
  rapidActivationPrice?: number;
  ddosProtectionPrice?: number;
  ddosProtectionCost?: number;
}

const monthlyToAnnual = (monthlyCost: number) => monthlyCost * 12;

const ipChargeFactory: Record<IPType, number | undefined> = {
  // cost per month * 12 for annual IPChoice costs
  [IPType.SUBNET_31]: 0,
  [IPType.SUBNET_30]: 0,
  [IPType.SUBNET_29]: monthlyToAnnual(8),
  [IPType.SUBNET_28]: monthlyToAnnual(24),
  [IPType.SUBNET_27]: monthlyToAnnual(56),
  [IPType.SUBNET_26]: monthlyToAnnual(120),
  [IPType.STATIC_16]: monthlyToAnnual(24),
  [IPType.STATIC_8]: monthlyToAnnual(8),
  [IPType.STATIC_4]: 0,
  [IPType.WAN]: 0,
};

export const getPrimaryIPCharge = (quote: IQuote) => {
  const primaryIPChoice = quote.location.aEnd.ip.selectedId;
  if (!primaryIPChoice) return 0;
  return toNum(ipChargeFactory[primaryIPChoice]);
};
export const getSecondaryIPCharge = (secondarySelectedPrice?: SelectedPriceProps) =>
  toNum(secondarySelectedPrice?.annual_ip_charge);

export const calculatePrimaryAnnualExtraTotals = (quote: IQuote, selectedPrice: SelectedPriceProps): AnnualExtras => {
  return {
    fttpAggregationCharge: selectedPrice?.fttp_aggregation_charge ?? 0,
    ipCost: selectedPrice?.annual_ip_charge ?? 0,
    mdiaAnnualPrice: selectedPrice?.mdia_annual_price ?? 0,
    annualDiscount: selectedPrice?.annual_discount ?? 0,
    rapidActivationPrice: getAdditionalPrice(selectedPrice.additional_prices, 'Rapid Activation Price')?.annual,
    ddosProtectionPrice: getAdditionalPrice(selectedPrice.additional_prices, `DDoS Protection - ${quote.ddosProtection} Price`)?.annual,
    ddosProtectionCost: getAdditionalPrice(selectedPrice.additional_prices, `DDoS Protection - ${quote.ddosProtection} Cost`)?.annual,
  };
};

export const calculateSecondaryAnnualExtraTotals = (
  secondarySelectedPrice: SelectedPriceProps,
  //selectedPrice: SelectedPriceProps,
  quote: IQuote
): AnnualExtras => {
  return {
    fttpAggregationCharge: secondarySelectedPrice?.fttp_aggregation_charge ?? 0,
    ipCost: getSecondaryIPCharge(secondarySelectedPrice),
    mdiaAnnualPrice: secondarySelectedPrice?.mdia_annual_price ?? 0,
    annualDiscount: secondarySelectedPrice?.annual_discount ?? 0,
    rapidActivationPrice: getAdditionalPrice(secondarySelectedPrice.additional_prices, 'Rapid Activation Price')?.annual,
    ddosProtectionPrice: getAdditionalPrice(secondarySelectedPrice.additional_prices, `DDoS Protection - ${quote.ddosProtection} Price`)?.annual,
    ddosProtectionCost: getAdditionalPrice(secondarySelectedPrice.additional_prices, `DDoS Protection - ${quote.ddosProtection} Cost`)?.annual,
  };
};

export const calculateCombinedAnnualExtraTotals = (
  selectedPrice: SelectedPriceProps,
  quote: IQuote,
  secondarySelectedPrice?: SelectedPriceProps
): AnnualExtras => {
  const ipCost = (selectedPrice.annual_ip_charge ?? 0) + getSecondaryIPCharge(secondarySelectedPrice);

  const fttpAggregationCharge = (selectedPrice?.fttp_aggregation_charge ?? 0) + (secondarySelectedPrice?.fttp_aggregation_charge ?? 0);
  const mdiaAnnualPrice = (selectedPrice?.mdia_annual_price ?? 0) + (secondarySelectedPrice?.mdia_annual_price ?? 0);
  const annualDiscount = (selectedPrice?.annual_discount ?? 0) + (secondarySelectedPrice?.annual_discount ?? 0);
  const rapidActivationPrice =
    (getAdditionalPrice(selectedPrice.additional_prices, 'Rapid Activation Price')?.annual ?? 0) +
    (getAdditionalPrice(secondarySelectedPrice?.additional_prices, 'Rapid Activation Price')?.annual ?? 0);
  const ddosProtectionPrice =
    (getAdditionalPrice(selectedPrice.additional_prices, `DDoS Protection - ${quote.ddosProtection} Price`)?.annual ?? 0) +
    (getAdditionalPrice(secondarySelectedPrice?.additional_prices, `DDoS Protection - ${quote.ddosProtection} Price`)?.annual ?? 0);
  const ddosProtectionCost =
    (getAdditionalPrice(selectedPrice.additional_prices, `DDoS Protection - ${quote.ddosProtection} Cost`)?.annual ?? 0) +
    (getAdditionalPrice(secondarySelectedPrice?.additional_prices, `DDoS Protection - ${quote.ddosProtection} Cost`)?.annual ?? 0);

  return {
    fttpAggregationCharge: fttpAggregationCharge,
    ipCost: ipCost,
    mdiaAnnualPrice: mdiaAnnualPrice,
    annualDiscount: annualDiscount,
    rapidActivationPrice: rapidActivationPrice,
    ddosProtectionPrice: ddosProtectionPrice,
    ddosProtectionCost: ddosProtectionCost,
  };
};
